/**
 * Analyse CA — Vue analytique complète (Sprint 85).
 *
 * Portage BE-TAYE avec 8 onglets analytiques :
 *  1. Vue d'ensemble : KPIs + projection année
 *  2. Mensuel : détail 12 mois + comparaison N/N-1
 *  3. Trimestriel / Annuel
 *  4. Top clients
 *  5. Catégories
 *  6. Heatmap saisonnalité
 *  7. Panier moyen + Croissance MoM/YoY/MA3
 *  8. Nouveau vs Récurrent
 */
import { useEffect, useState, useMemo, useCallback } from "react";
import { motion } from "framer-motion";
import api, { apiError } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  BarChart, Bar, LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip,
  ResponsiveContainer, Legend, ComposedChart, PieChart, Pie, Cell,
} from "recharts";
import {
  TrendingUp, TrendingDown, ArrowUpRight, Users, Target, Calendar,
  Percent, Layers, Sparkles, RefreshCw,
} from "lucide-react";
import { toast } from "sonner";
import { cn } from "@/lib/utils";

const CURR_YEAR = new Date().getFullYear();
const PALETTE = ["#3b82f6", "#8b5cf6", "#10b981", "#f59e0b", "#ef4444",
  "#06b6d4", "#ec4899", "#84cc16", "#6366f1"];

const fmtEuro = (n, p = 0) =>
  `${(Number(n) || 0).toLocaleString("fr-FR", {
    minimumFractionDigits: p, maximumFractionDigits: p,
  })} €`;
const fmtMonth = (mk) => {
  if (!mk) return "";
  const [y, m] = String(mk).split("-");
  return new Date(Number(y), Number(m) - 1, 1)
    .toLocaleDateString("fr-FR", { month: "short" }).replace(".", "");
};

function Kpi({ label, value, delta, icon: Icon, accent = "blue", idx = 0, hint }) {
  const accents = {
    blue: "bg-blue-50 text-blue-700",
    emerald: "bg-emerald-50 text-emerald-700",
    amber: "bg-amber-50 text-amber-700",
    violet: "bg-violet-50 text-violet-700",
    rose: "bg-rose-50 text-rose-700",
  };
  const positive = typeof delta === "number" && delta >= 0;
  return (
    <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }}
      transition={{ delay: idx * 0.04 }}>
      <Card className="bg-white border-slate-200 rounded-xl p-5 card-hover">
        <div className="flex items-center justify-between mb-3">
          <div className={cn("h-9 w-9 rounded-lg flex items-center justify-center", accents[accent])}>
            <Icon className="h-4 w-4" strokeWidth={1.75} />
          </div>
          {typeof delta === "number" && (
            <div className={cn("flex items-center gap-1 text-xs font-medium",
              positive ? "text-emerald-700" : "text-red-700")}>
              {positive ? <TrendingUp className="h-3.5 w-3.5" /> : <TrendingDown className="h-3.5 w-3.5" />}
              {positive ? "+" : ""}{delta.toFixed(1)} %
            </div>
          )}
        </div>
        <div className="text-[10px] font-medium uppercase tracking-widest text-slate-500">{label}</div>
        <div className="mt-1 text-2xl font-semibold tracking-tight text-slate-900 font-mono tabular-nums"
             style={{ fontFamily: "Manrope, sans-serif" }}>{value}</div>
        {hint && <div className="mt-1 text-xs text-slate-500">{hint}</div>}
      </Card>
    </motion.div>
  );
}

function ChartCard({ title, subtitle, children, testid }) {
  return (
    <Card className="border-slate-200 bg-white rounded-xl p-5" data-testid={testid}>
      <div className="mb-4">
        <div className="text-[10px] font-medium uppercase tracking-widest text-slate-500">{title}</div>
        {subtitle && <div className="text-sm text-slate-500">{subtitle}</div>}
      </div>
      {children}
    </Card>
  );
}

export default function AnalyseCA() {
  const [year, setYear] = useState(CURR_YEAR);
  const [tab, setTab] = useState("overview");
  const [loading, setLoading] = useState(true);
  const [target, setTarget] = useState(10000);
  const [targetInput, setTargetInput] = useState("");
  const [overview, setOverview] = useState(null);
  const [monthly, setMonthly] = useState([]);
  const [yearlyCmp, setYearlyCmp] = useState({});
  const [topClients, setTopClients] = useState([]);
  const [cats, setCats] = useState([]);
  const [heat, setHeat] = useState([]);
  const [basket, setBasket] = useState([]);
  const [growth, setGrowth] = useState([]);
  const [nvr, setNvr] = useState([]);
  const [projection, setProjection] = useState(null);
  const [quarterly, setQuarterly] = useState([]);

  const loadAll = useCallback(async () => {
    setLoading(true);
    try {
      const [ov, mo, cmp, tc, ca, ht, bk, gr, nv, pj, qr, tg] = await Promise.all([
        api.get(`/stats/overview?year=${year}`),
        api.get(`/stats/monthly?year=${year}`),
        api.get(`/stats/yearly-comparison?year=${year}`),
        api.get("/stats/top-clients?limit=15"),
        api.get("/stats/categories"),
        api.get("/stats/heatmap"),
        api.get("/stats/average-basket"),
        api.get("/stats/growth"),
        api.get("/stats/new-vs-recurring"),
        api.get("/stats/projection"),
        api.get("/stats/quarterly"),
        api.get("/stats/sales-target"),
      ]);
      setOverview(ov.data);
      setMonthly(mo.data.data || []);
      setYearlyCmp(cmp.data || {});
      setTopClients(tc.data || []);
      setCats(ca.data || []);
      setHeat(ht.data || []);
      setBasket(bk.data || []);
      setGrowth(gr.data || []);
      setNvr(nv.data || []);
      setProjection(pj.data);
      setQuarterly(qr.data || []);
      setTarget(tg.data.target);
      setTargetInput(String(tg.data.target));
    } catch (e) { toast.error(apiError(e)); }
    finally { setLoading(false); }
  }, [year]);

  useEffect(() => { loadAll(); }, [loadAll]);

  const saveTarget = async () => {
    try {
      await api.put(`/stats/sales-target`, { target: Number(targetInput) });
      setTarget(Number(targetInput));
      toast.success("Objectif mensuel enregistré");
    } catch (e) { toast.error(apiError(e)); }
  };

  const monthlyChart = useMemo(() => monthly.map((m) => {
    const y = String(year);
    const py = String(year - 1);
    const prev = (yearlyCmp?.[py] || {})[m.month] || 0;
    const curr = (yearlyCmp?.[y] || {})[m.month] || m.factures_ht;
    return {
      name: fmtMonth(`${year}-${m.month}`),
      curr, prev, avoirs: Math.abs(m.avoirs_ht) || 0,
      net: m.net_ht,
    };
  }), [monthly, yearlyCmp, year]);

  const heatData = useMemo(() => {
    const years = Array.from(new Set(heat.map((r) => r.year))).sort();
    return years.map((y) => {
      const row = { year: y };
      for (let m = 1; m <= 12; m++) {
        const mk = String(m).padStart(2, "0");
        row[`m${mk}`] = (heat.find((r) => r.year === y && r.month === mk) || {}).total_ht || 0;
      }
      return row;
    });
  }, [heat]);
  const heatMax = useMemo(
    () => Math.max(1, ...heat.map((r) => r.total_ht)),
    [heat]
  );

  return (
    <div className="space-y-6" data-testid="analyse-ca-page">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h1 className="text-3xl font-semibold tracking-tight text-slate-900"
              style={{ fontFamily: "Manrope, sans-serif" }}>Analyse CA</h1>
          <p className="mt-1 text-sm text-slate-500">
            Vision analytique du chiffre d'affaires · {overview?.year || year}
            {overview?.month && ` · mois ${overview.month}`}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Input type="number" value={year} min={2020} max={CURR_YEAR}
                  onChange={(e) => setYear(Number(e.target.value))}
                  className="w-28" data-testid="year-input" />
          <Button variant="outline" size="sm" onClick={loadAll} data-testid="reload-btn">
            <RefreshCw className="h-4 w-4 mr-1" /> Rafraîchir
          </Button>
        </div>
      </div>

      {/* ==== KPIs principaux ==== */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <Kpi idx={0} label={`CA ${year}`} value={fmtEuro(overview?.factures_ht)}
             delta={overview?.ca_trend_pct} icon={ArrowUpRight} accent="blue"
             hint={`${overview?.factures_count || 0} factures`} />
        <Kpi idx={1} label="CA Net (- avoirs)" value={fmtEuro(overview?.net_ht)}
             icon={TrendingUp} accent="emerald"
             hint={overview?.avoirs_count ? `${overview.avoirs_count} avoir(s)` : "aucun avoir"} />
        <Kpi idx={2} label="Encours impayés" value={fmtEuro(overview?.outstanding_ttc)}
             icon={Calendar} accent="amber"
             hint={`${overview?.outstanding_count || 0} facture(s)`} />
        <Kpi idx={3} label="Clients uniques" value={overview?.unique_clients || 0}
             icon={Users} accent="violet"
             hint={overview?.avg_payment_delay_days
               ? `Délai moy. ${overview.avg_payment_delay_days} j`
               : "Délai n/a"} />
      </div>

      {/* ==== Projection + Objectif ==== */}
      {projection && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          <Card className="border-slate-200 bg-white rounded-xl p-5" data-testid="projection-card">
            <div className="flex items-center gap-3 mb-3">
              <div className="h-9 w-9 rounded-lg bg-blue-50 text-blue-700 flex items-center justify-center">
                <Sparkles className="h-4 w-4" />
              </div>
              <div>
                <div className="text-[10px] font-medium uppercase tracking-widest text-slate-500">Projection {projection.year}</div>
                <div className="text-sm text-slate-500">Basée sur {projection.months_elapsed} mois écoulés</div>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <div className="text-xs text-slate-500">Cumul annuel</div>
                <div className="text-xl font-semibold font-mono tabular-nums">{fmtEuro(projection.ytd_ca)}</div>
              </div>
              <div>
                <div className="text-xs text-slate-500">Projection annuelle</div>
                <div className="text-xl font-semibold text-blue-700 font-mono tabular-nums">{fmtEuro(projection.projected_ca)}</div>
              </div>
              <div>
                <div className="text-xs text-slate-500">{projection.year - 1} (total)</div>
                <div className="text-sm font-mono tabular-nums">{fmtEuro(projection.prev_year_ca)}</div>
              </div>
              <div>
                <div className="text-xs text-slate-500">Croissance projetée</div>
                {typeof projection.projected_growth_pct === "number" ? (
                  <Badge className={projection.projected_growth_pct >= 0
                    ? "bg-emerald-100 text-emerald-700"
                    : "bg-rose-100 text-rose-700"}>
                    {projection.projected_growth_pct >= 0 ? "+" : ""}{projection.projected_growth_pct} %
                  </Badge>
                ) : <Badge variant="outline">n/a</Badge>}
              </div>
            </div>
          </Card>

          <Card className="border-slate-200 bg-white rounded-xl p-5" data-testid="target-card">
            <div className="flex items-center gap-3 mb-3">
              <div className="h-9 w-9 rounded-lg bg-violet-50 text-violet-700 flex items-center justify-center">
                <Target className="h-4 w-4" />
              </div>
              <div>
                <div className="text-[10px] font-medium uppercase tracking-widest text-slate-500">Objectif mensuel</div>
                <div className="text-sm text-slate-500">Cible commerciale à atteindre chaque mois</div>
              </div>
            </div>
            <div className="flex gap-2">
              <Input type="number" value={targetInput} onChange={(e) => setTargetInput(e.target.value)}
                      placeholder="10000" data-testid="target-input" />
              <Button onClick={saveTarget} data-testid="save-target-btn">Enregistrer</Button>
            </div>
            <p className="mt-2 text-xs text-slate-500">Actuel : <strong>{fmtEuro(target)}</strong> · Annuel : {fmtEuro(target * 12)}</p>
          </Card>
        </div>
      )}

      {/* ==== Onglets analytiques ==== */}
      <Tabs value={tab} onValueChange={setTab}>
        <TabsList className="bg-slate-100 flex-wrap h-auto">
          <TabsTrigger value="overview" data-testid="tab-monthly">Mensuel</TabsTrigger>
          <TabsTrigger value="quarterly" data-testid="tab-quarterly">Trimestriel</TabsTrigger>
          <TabsTrigger value="top" data-testid="tab-top">Top clients</TabsTrigger>
          <TabsTrigger value="cats" data-testid="tab-cats">Catégories</TabsTrigger>
          <TabsTrigger value="heat" data-testid="tab-heat">Heatmap</TabsTrigger>
          <TabsTrigger value="growth" data-testid="tab-growth">Croissance</TabsTrigger>
          <TabsTrigger value="nvr" data-testid="tab-nvr">Nouveau vs Récurrent</TabsTrigger>
        </TabsList>

        {/* Mensuel */}
        <TabsContent value="overview" className="mt-4">
          <ChartCard title={`CA mensuel ${year} vs ${year - 1}`} subtitle="Comparaison N/N-1 mois par mois" testid="chart-monthly">
            <ResponsiveContainer width="100%" height={360}>
              <ComposedChart data={monthlyChart}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                <XAxis dataKey="name" stroke="#94a3b8" fontSize={12} />
                <YAxis stroke="#94a3b8" fontSize={12} tickFormatter={(v) => fmtEuro(v)} />
                <Tooltip formatter={(v) => fmtEuro(v)}
                  contentStyle={{ background: "white", border: "1px solid #e2e8f0", borderRadius: 8 }} />
                <Legend />
                <Bar dataKey="curr" fill="#3b82f6" radius={[6, 6, 0, 0]} name={`${year}`} />
                <Line type="monotone" dataKey="prev" stroke="#94a3b8" strokeWidth={2}
                  strokeDasharray="5 5" dot={{ r: 3 }} name={`${year - 1}`} />
              </ComposedChart>
            </ResponsiveContainer>
          </ChartCard>
        </TabsContent>

        {/* Trimestriel */}
        <TabsContent value="quarterly" className="mt-4">
          <ChartCard title="CA par trimestre" subtitle="Toutes années" testid="chart-quarterly">
            <ResponsiveContainer width="100%" height={360}>
              <BarChart data={quarterly.map((r) => ({ name: `${r.year} ${r.quarter}`, total_ht: r.total_ht }))}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                <XAxis dataKey="name" stroke="#94a3b8" fontSize={12} />
                <YAxis stroke="#94a3b8" fontSize={12} tickFormatter={(v) => fmtEuro(v)} />
                <Tooltip formatter={(v) => fmtEuro(v)} />
                <Bar dataKey="total_ht" fill="#6366f1" radius={[6, 6, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </ChartCard>
        </TabsContent>

        {/* Top clients */}
        <TabsContent value="top" className="mt-4">
          <ChartCard title="Top clients" subtitle="Sur toute la période" testid="chart-top">
            <ResponsiveContainer width="100%" height={420}>
              <BarChart data={topClients.slice(0, 15).map((c) => ({ name: (c.client || "").slice(0, 26), value: c.total_ht, count: c.count }))}
                         layout="vertical" margin={{ left: 120 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                <XAxis type="number" stroke="#94a3b8" fontSize={12} tickFormatter={(v) => fmtEuro(v)} />
                <YAxis type="category" dataKey="name" stroke="#94a3b8" fontSize={11} width={160} />
                <Tooltip formatter={(v) => fmtEuro(v)}
                  contentStyle={{ background: "white", border: "1px solid #e2e8f0", borderRadius: 8 }} />
                <Bar dataKey="value" fill="#8b5cf6" radius={[0, 6, 6, 0]} name="CA HT" />
              </BarChart>
            </ResponsiveContainer>
          </ChartCard>
        </TabsContent>

        {/* Catégories */}
        <TabsContent value="cats" className="mt-4">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            <ChartCard title="Répartition par catégorie" subtitle="Détection par mots-clés" testid="chart-cats-pie">
              <ResponsiveContainer width="100%" height={340}>
                <PieChart>
                  <Pie data={cats} dataKey="total_ht" nameKey="category" cx="50%" cy="50%"
                        outerRadius={100} label={(e) => e.category}>
                    {cats.map((_, i) => <Cell key={i} fill={PALETTE[i % PALETTE.length]} />)}
                  </Pie>
                  <Tooltip formatter={(v) => fmtEuro(v)} />
                </PieChart>
              </ResponsiveContainer>
            </ChartCard>
            <ChartCard title="Détail catégories" subtitle="Trié par CA décroissant" testid="chart-cats-bar">
              <div className="space-y-2">
                {cats.map((c, i) => (
                  <div key={c.category} className="flex items-center gap-3">
                    <div className="w-3 h-3 rounded-full" style={{ background: PALETTE[i % PALETTE.length] }} />
                    <div className="flex-1 text-sm text-slate-700">{c.category}</div>
                    <div className="text-sm font-mono tabular-nums font-semibold">{fmtEuro(c.total_ht)}</div>
                  </div>
                ))}
                {!cats.length && <p className="text-sm text-slate-500">Aucune donnée</p>}
              </div>
            </ChartCard>
          </div>
        </TabsContent>

        {/* Heatmap */}
        <TabsContent value="heat" className="mt-4">
          <ChartCard title="Heatmap saisonnalité" subtitle="Intensité CA par mois × année" testid="chart-heatmap">
            <div className="overflow-x-auto">
              <table className="w-full text-xs">
                <thead>
                  <tr>
                    <th className="text-left p-2 text-slate-500">Année</th>
                    {["Jan", "Fév", "Mar", "Avr", "Mai", "Juin", "Juil", "Août", "Sep", "Oct", "Nov", "Déc"].map((m) => (
                      <th key={m} className="p-2 text-slate-500 font-medium">{m}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {heatData.map((row) => (
                    <tr key={row.year}>
                      <td className="p-2 font-semibold">{row.year}</td>
                      {Array.from({ length: 12 }, (_, i) => {
                        const val = row[`m${String(i + 1).padStart(2, "0")}`] || 0;
                        const intensity = val / heatMax;
                        const bg = intensity > 0
                          ? `rgba(59, 130, 246, ${0.15 + intensity * 0.7})`
                          : "#f1f5f9";
                        return (
                          <td key={i} className="p-1">
                            <div className="rounded p-2 text-center font-mono tabular-nums text-[11px]"
                                  style={{ background: bg, color: intensity > 0.5 ? "white" : "#334155" }}>
                              {val > 0 ? fmtEuro(val) : "—"}
                            </div>
                          </td>
                        );
                      })}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </ChartCard>
        </TabsContent>

        {/* Croissance */}
        <TabsContent value="growth" className="mt-4">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            <ChartCard title="Croissance MoM / YoY" subtitle="Variation mois par mois et année par année" testid="chart-growth">
              <ResponsiveContainer width="100%" height={340}>
                <LineChart data={growth.slice(-24)}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                  <XAxis dataKey="month" stroke="#94a3b8" fontSize={11} />
                  <YAxis stroke="#94a3b8" fontSize={12} tickFormatter={(v) => `${v}%`} />
                  <Tooltip formatter={(v) => (v == null ? "n/a" : `${v}%`)} />
                  <Legend />
                  <Line type="monotone" dataKey="mom" stroke="#3b82f6" strokeWidth={2} dot={{ r: 3 }} name="MoM %" />
                  <Line type="monotone" dataKey="yoy" stroke="#10b981" strokeWidth={2} dot={{ r: 3 }} name="YoY %" />
                </LineChart>
              </ResponsiveContainer>
            </ChartCard>
            <ChartCard title="Panier moyen" subtitle="CA / nombre de factures par mois" testid="chart-basket">
              <ResponsiveContainer width="100%" height={340}>
                <BarChart data={basket.slice(-12)}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                  <XAxis dataKey="month" stroke="#94a3b8" fontSize={11} />
                  <YAxis stroke="#94a3b8" fontSize={12} tickFormatter={(v) => fmtEuro(v)} />
                  <Tooltip formatter={(v) => fmtEuro(v)} />
                  <Bar dataKey="average" fill="#f59e0b" radius={[6, 6, 0, 0]} name="Panier moyen" />
                </BarChart>
              </ResponsiveContainer>
            </ChartCard>
          </div>
        </TabsContent>

        {/* Nouveau vs Récurrent */}
        <TabsContent value="nvr" className="mt-4">
          <ChartCard title="Acquisition vs Récurrent" subtitle="CA nouveaux clients vs clients existants" testid="chart-nvr">
            <ResponsiveContainer width="100%" height={360}>
              <BarChart data={nvr.slice(-12)}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                <XAxis dataKey="month" stroke="#94a3b8" fontSize={11} />
                <YAxis stroke="#94a3b8" fontSize={12} tickFormatter={(v) => fmtEuro(v)} />
                <Tooltip formatter={(v) => fmtEuro(v)} />
                <Legend />
                <Bar dataKey="new_ca" stackId="a" fill="#10b981" radius={[0, 0, 0, 0]} name="Nouveaux clients" />
                <Bar dataKey="recurring_ca" stackId="a" fill="#3b82f6" radius={[6, 6, 0, 0]} name="Clients récurrents" />
              </BarChart>
            </ResponsiveContainer>
          </ChartCard>
        </TabsContent>
      </Tabs>

      {loading && (
        <Card className="border-slate-200 bg-white rounded-xl p-6 text-center text-slate-500 text-sm">
          Chargement des analytics…
        </Card>
      )}
    </div>
  );
}
